import { type FC, useState, useEffect } from 'react';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { VimeoVideo } from '../../respShared/PdpRespSharedHeroCarousel/CarouselItems/VimeoVideo.tsx';
import { type CarouselItem } from './types.ts';

import styles from './VideoOverlay.module.css';

export const useVideoOverlay = ({
    carouselItems,
    activeSlide,
    previewIndex,
}: {
    carouselItems: CarouselItem[];
    activeSlide: number;
    previewIndex: number | null;
}): {
    videoItem: CarouselItem | null;
    videoMounted: boolean;
    showVideo: boolean;
    isVideoActive: boolean;
    isOtherThumbHovered: boolean;
    videoPaused: boolean;
} => {
    const videoIndex = carouselItems.findIndex(ci => ci.__type === 'video');
    const videoItem = videoIndex >= 0 ? carouselItems[videoIndex] : null;
    const isVideoActive = activeSlide - 1 === videoIndex;
    const isVideoHovered = previewIndex === videoIndex;
    const showVideo = isVideoHovered || isVideoActive;

    const [videoMounted, setVideoMounted] = useState(false);
    useEffect(() => {
        if (showVideo && !videoMounted) {
            setVideoMounted(true);
        }
    }, [showVideo, videoMounted]);

    const isOtherThumbHovered = previewIndex !== null && previewIndex !== videoIndex;
    const videoPaused = isVideoActive && isOtherThumbHovered;

    return { videoItem, videoMounted, showVideo, isVideoActive, isOtherThumbHovered, videoPaused };
};

export const VideoOverlay: FC<{
    videoItem: CarouselItem | null;
    videoMounted: boolean;
    showVideo: boolean;
    isVideoActive: boolean;
    isOtherThumbHovered: boolean;
    videoPaused: boolean;
}> = ({ videoItem, videoMounted, showVideo, isVideoActive, isOtherThumbHovered, videoPaused }) => {
    if (!videoMounted || videoItem?.__type !== 'video') {
        return null;
    }

    return (
        <div
            className={classnames(
                dibsCss.absolute,
                dibsCss.inset0,
                dibsCss.zBump,
                dibsCss.bgMoonstone,
                dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.justifyCenter,
                (!showVideo || isOtherThumbHovered) && dibsCss.invisible,
                showVideo && !isVideoActive && styles.previewOverlay
            )}
        >
            <VimeoVideo videoVimeo={videoItem} heightOffset={0} paused={videoPaused} />
        </div>
    );
};
